import * as React from 'react'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import CardMedia from '@mui/material/CardMedia'
import Typography from '@mui/material/Typography'
import { Button, CardActions, Chip, Divider } from '@mui/material'
import useIntersectionObserver from '../userIntersectionObserver'
import './experience_component.scss'

function parseDate (dateString) {
  if (!dateString) return new Date()
  const [year, month, day] = dateString.split('-').map(Number)
  return new Date(Date.UTC(year, month - 1, day))
}

function formatDate (dateString) {
  if (!dateString) return 'Present'
  return new Intl.DateTimeFormat('en', {
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC'
  }).format(parseDate(dateString))
}

function diffMonths (startDateString, endDateString) {
  const start = parseDate(startDateString)
  const end = endDateString ? parseDate(endDateString) : new Date()

  let months =
    (end.getUTCFullYear() - start.getUTCFullYear()) * 12 +
    (end.getUTCMonth() - start.getUTCMonth())

  if (end.getUTCDate() < start.getUTCDate()) months -= 1
  return Math.max(0, months)
}

function formatDuration (startDateString, endDateString) {
  const totalMonths = diffMonths(startDateString, endDateString)
  const years = Math.floor(totalMonths / 12)
  const months = totalMonths % 12
  const parts = []

  if (years) parts.push(`${years} ${years === 1 ? 'yr' : 'yrs'}`)
  if (months) parts.push(`${months} ${months === 1 ? 'mo' : 'mos'}`)
  if (!parts.length) parts.push('<1 mo')

  return parts.join(' ')
}

function getRolePeriod (role) {
  if (!role.startDate) return role.period || 'Add start date'

  const start = formatDate(role.startDate)
  const end = formatDate(role.endDate)
  const duration = formatDuration(role.startDate, role.endDate)
  return `${start} — ${end} · ${duration}`
}

export default function ExperienceComponent (props) {
  const [ref, isVisible] = useIntersectionObserver({ threshold: 0.1 }, 250)

  const currentOrganization = props.roles.some(role => !role.endDate)

  return (
    <div ref={ref} className={`experience_org ${isVisible ? 'is_visible' : ''}`}>
      <Card className='experience_org_card'>
        <div className='experience_org_header'>
          <div className='experience_org_identity'>
            <div className='experience_org_index'>ORG / 01</div>
            <Typography className='experience_org_name' variant='h3' component='h2'>
              {props.company}
            </Typography>
            <div className='experience_org_status'>
              <span className='experience_status_dot' />
              {currentOrganization ? 'CURRENT ORGANIZATION' : 'PAST ORGANIZATION'}
            </div>
          </div>
          <Button
            className='experience_org_link'
            size='small'
            onClick={() => window.open(props.link, '_blank', 'noopener,noreferrer')}
          >
            ORGANIZATION ↗
          </Button>
        </div>

        <div className='experience_org_grid'>
          <div className='experience_org_media_frame'>
            <CardMedia
              component='img'
              image={props.imagePath}
              alt={`${props.company} office`}
              className='experience_org_media'
              loading='lazy'
            />
          </div>

          <CardContent className='experience_org_content'>
            <Typography className='experience_org_summary' component='p'>
              {props.summary}
            </Typography>

            <div className='experience_role_list'>
              {props.roles.map((role, index) => (
                <React.Fragment key={`${role.title}-${role.location}-${role.startDate}-${index}`}>
                  <div className='experience_role'>
                    <div className='experience_role_marker'>{String(props.roles.length - index).padStart(2, '0')}</div>
                    <div className='experience_role_body'>
                      <div className='experience_role_topline'>
                        <Typography className='experience_role_title' variant='h5' component='h3'>
                          {role.title}
                        </Typography>
                        <span className='experience_role_period'>
                          {getRolePeriod(role)}
                        </span>
                      </div>
                      <div className='experience_role_meta'>
                        <Chip label={role.team} size='small' />
                        <span>{role.location}</span>
                      </div>
                      <Typography className='experience_role_description' component='p'>
                        {role.description}
                      </Typography>
                    </div>
                  </div>
                  {index < props.roles.length - 1 && <Divider className='experience_role_divider' />}
                </React.Fragment>
              ))}
            </div>
          </CardContent>
        </div>

        <CardActions className='experience_org_footer'>
          <span>{props.roles.length} ROLES / 01 ORGANIZATION</span>
          <span>END DATE BLANK = LIVE DURATION</span>
        </CardActions>
      </Card>
    </div>
  )
}
